<template>
  <div
    class="scroll-to-top-wrap mobile-frame-fixed fixed"
    :style="wrapStyle"
  >
    <Transition name="scroll-to-top-fade">
      <button
        v-if="visible"
        type="button"
        class="scroll-to-top-btn"
        :aria-label="ariaLabel"
        @click="handleClick"
      >
        <svg class="scroll-to-top-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 14l4-4 4 4" />
        </svg>
      </button>
    </Transition>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { resetPageScroll } from '@/utils/scrollReset'
import { resetTelegramScroll } from '@/utils/telegramWebAppClient'
import { useTelegramWebApp } from '@/composables/useTelegramWebApp'

const props = defineProps({
  threshold: {
    type: Number,
    default: 480,
  },
  bottom: {
    type: String,
    default: '1.25rem',
  },
  extraBottom: {
    type: Number,
    default: 0,
  },
  zIndex: {
    type: Number,
    default: 110,
  },
  scrollTarget: {
    type: String,
    default: '',
  },
  smooth: {
    type: Boolean,
    default: true,
  },
  idleDelay: {
    type: Number,
    default: 320,
  },
})

const { t } = useI18n()
const { hapticFeedback } = useTelegramWebApp()

const visible = ref(false)
let scrollElement = null
let rafId = 0
let idleTimer = null

const ariaLabel = computed(() => t('Scroll_To_Top'))

const wrapStyle = computed(() => ({
  bottom: `calc(${props.bottom} + ${props.extraBottom}px + max(env(safe-area-inset-bottom, 0px), var(--tg-content-safe-bottom, 0px)))`,
  zIndex: props.zIndex,
}))

const getScrollTop = () => {
  if (scrollElement) return scrollElement.scrollTop || 0
  return window.scrollY
    || document.documentElement?.scrollTop
    || document.body?.scrollTop
    || 0
}

const shouldShow = () => getScrollTop() > props.threshold

const revealAfterIdle = () => {
  clearTimeout(idleTimer)
  idleTimer = window.setTimeout(() => {
    visible.value = shouldShow()
  }, props.idleDelay)
}

const onScroll = () => {
  if (!shouldShow()) {
    clearTimeout(idleTimer)
    visible.value = false
    return
  }

  visible.value = false
  revealAfterIdle()

  if (rafId) return
  rafId = requestAnimationFrame(() => {
    rafId = 0
  })
}

const scrollWindowToTop = () => {
  const behavior = props.smooth ? 'smooth' : 'instant'

  try {
    window.scrollTo({ top: 0, left: 0, behavior })
  } catch (_) {
    resetPageScroll()
    return
  }

  resetTelegramScroll()

  if (document.documentElement) {
    if (props.smooth) {
      document.documentElement.scrollTo({ top: 0, behavior })
    } else {
      document.documentElement.scrollTop = 0
    }
  }

  if (document.body) {
    document.body.scrollTop = 0
  }

  const app = document.getElementById('app')
  if (app) {
    app.scrollTop = 0
  }

  if (props.smooth) {
    window.setTimeout(() => {
      resetTelegramScroll()
      if (getScrollTop() > 0) {
        resetPageScroll()
      }
    }, 400)
  }
}

const scrollContainerToTop = () => {
  if (!scrollElement) return

  try {
    scrollElement.scrollTo({
      top: 0,
      left: 0,
      behavior: props.smooth ? 'smooth' : 'instant',
    })
  } catch (_) {
    scrollElement.scrollTop = 0
  }
}

const handleClick = () => {
  hapticFeedback?.('impact', 'light')
  visible.value = false
  clearTimeout(idleTimer)

  if (scrollElement) {
    scrollContainerToTop()
  } else {
    scrollWindowToTop()
  }
}

const resolveScrollElement = () => {
  scrollElement = props.scrollTarget
    ? document.querySelector(props.scrollTarget)
    : null
}

const bindScrollListener = () => {
  resolveScrollElement()
  const target = scrollElement || window
  target.addEventListener('scroll', onScroll, { passive: true })
  visible.value = shouldShow()
}

const unbindScrollListener = () => {
  const target = scrollElement || window
  target.removeEventListener('scroll', onScroll)
  clearTimeout(idleTimer)
  if (rafId) {
    cancelAnimationFrame(rafId)
    rafId = 0
  }
}

onMounted(() => {
  bindScrollListener()
})

onUnmounted(() => {
  unbindScrollListener()
})
</script>

<style scoped>
.scroll-to-top-wrap {
  pointer-events: none;
}

.scroll-to-top-btn {
  pointer-events: auto;
  position: absolute;
  right: 1rem;
  bottom: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  padding: 0;
  border: 1px solid rgba(139, 92, 246, 0.14);
  border-radius: 9999px;
  color: #8b5cf6;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow:
    0 2px 8px rgba(15, 23, 42, 0.06),
    0 1px 2px rgba(15, 23, 42, 0.04);
  cursor: pointer;
  transition: transform 0.12s ease-out, box-shadow 0.12s ease-out, background-color 0.12s ease-out, border-color 0.12s ease-out, color 0.12s ease-out;
  -webkit-tap-highlight-color: transparent;
}

.scroll-to-top-btn:active {
  transform: scale(0.94);
  color: #7c3aed;
  background: rgba(237, 233, 254, 0.95);
  border-color: rgba(139, 92, 246, 0.22);
  box-shadow:
    0 2px 6px rgba(124, 58, 237, 0.1),
    0 1px 2px rgba(15, 23, 42, 0.04);
}

@media (hover: hover) and (pointer: fine) {
  .scroll-to-top-btn {
    transition: transform 0.2s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  }

  .scroll-to-top-btn:hover,
  .scroll-to-top-btn:focus-visible {
    color: #7c3aed;
    background: rgba(255, 255, 255, 0.96);
    border-color: rgba(139, 92, 246, 0.24);
    box-shadow:
      0 4px 14px rgba(124, 58, 237, 0.1),
      0 2px 6px rgba(15, 23, 42, 0.06);
  }
}

.scroll-to-top-icon {
  width: 1rem;
  height: 1rem;
  opacity: 0.88;
}

:global(html.dark .scroll-to-top-btn) {
  color: #c4b5fd;
  background: rgba(17, 24, 39, 0.82);
  border-color: rgba(139, 92, 246, 0.2);
  box-shadow:
    0 2px 10px rgba(0, 0, 0, 0.22),
    0 1px 2px rgba(0, 0, 0, 0.12);
}

:global(html.dark .scroll-to-top-btn:active) {
  color: #ddd6fe;
  background: rgba(55, 48, 163, 0.22);
  border-color: rgba(167, 139, 250, 0.28);
}

@media (hover: hover) and (pointer: fine) {
  :global(html.dark .scroll-to-top-btn:hover),
  :global(html.dark .scroll-to-top-btn:focus-visible) {
    color: #ddd6fe;
    background: rgba(31, 41, 55, 0.92);
    border-color: rgba(167, 139, 250, 0.32);
  }
}

.scroll-to-top-fade-enter-active,
.scroll-to-top-fade-leave-active {
  transition: opacity 0.18s ease-out;
}

.scroll-to-top-fade-enter-from,
.scroll-to-top-fade-leave-to {
  opacity: 0;
}

@media (hover: hover) and (pointer: fine) {
  .scroll-to-top-fade-enter-active,
  .scroll-to-top-fade-leave-active {
    transition: opacity 0.24s cubic-bezier(0.22, 1, 0.36, 1), transform 0.24s cubic-bezier(0.22, 1, 0.36, 1);
  }

  .scroll-to-top-fade-enter-from,
  .scroll-to-top-fade-leave-to {
    transform: translateY(10px) scale(0.9);
  }
}

@media (prefers-reduced-motion: reduce) {
  .scroll-to-top-btn {
    transition: none;
  }

  .scroll-to-top-fade-enter-active,
  .scroll-to-top-fade-leave-active {
    transition: opacity 0.12s ease;
  }

  .scroll-to-top-fade-enter-from,
  .scroll-to-top-fade-leave-to {
    transform: none;
  }
}
</style>
